import { SxProp, View, styled } from 'dripsy';
import { Pressable } from 'react-native';
import React from 'react';
import { Space } from '@/design-system/components/Space';
import { H6, TextAmount, TextBody } from '@/design-system/components/typography/UniTypography';
import { SkeletonView, UniSkeleton } from '@/design-system/components/cards';

const PressableView = styled(Pressable)();
export interface AmountSummaryProps {
  label: string;
  asset?: string;
  value: string;
  success?: boolean;
  tooltipText?: Array<string>;
  alignRight?: boolean;
  showValue?: boolean;
  sx?: SxProp;
  amountSx?: SxProp;
  loading?: boolean;
}

export const AmountSummary: React.FC<AmountSummaryProps> = ({
  label,
  asset,
  value,
  success,
  tooltipText,
  alignRight,
  showValue = true,
  sx,
  amountSx,
  loading
}) => {
  const dashedBorderBottom: SxProp = { borderBottomWidth: 1, borderBottomStyle: 'dashed', borderBottomColor: '$borderButton' };

  return (
    <View
      sx={{
        ...styles.container,
        alignItems: alignRight ? 'flex-end' : 'flex-start',
        ...sx
      }}>
      <TextBody secondary semiBold>
        {label}
      </TextBody>
      <Space height="$1" />
      {loading ? <SkeletonView height={24} width={180} /> : null}
      {!loading && asset ? (
        <PressableView>
          {({ hovered }) => (
            <View sx={styles.textContainer}>
              <TextAmount
                size="H6"
                weight="bold"
                currency={asset}
                success={success}
                sx={{ ...(tooltipText && dashedBorderBottom), color: '$textThemeSecondary', ...amountSx }}
                decimals={['USD', 'HKD', '%'].includes(asset) ? 2 : 8}
                shouldHide={!showValue}>
                {value}
              </TextAmount>
              {tooltipText && hovered && (
                <View sx={styles.tooltip}>
                  {tooltipText.map((text, index) => (
                    <TextBody key={`tooltip-text-${index}-${text}`} sx={styles.tooltipText}>
                      {showValue ? text : '*********'}
                    </TextBody>
                  ))}
                  <View sx={styles.tooltipArrow} />
                </View>
              )}
            </View>
          )}
        </PressableView>
      ) : null}
      {!loading && !asset ? <H6 bold>{value}</H6> : null}
    </View>
  );
};

enum StyleName {
  container = 'container',
  textContainer = 'textContainer',
  tooltip = 'tooltip',
  tooltipText = 'tooltipText',
  tooltipArrow = 'tooltipArrow'
}

const styles: Record<StyleName, SxProp> = {
  container: {},
  textContainer: {},
  tooltip: {
    position: 'absolute',
    top: '$7',
    padding: '$3',
    borderRadius: 2,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    zIndex: 1,
    alignItems: 'center'
  },
  tooltipText: {
    width: '100%',
    maxWidth: 200,
    color: '$textTooltip',
    whiteSpace: 'nowrap'
  },
  tooltipArrow: {
    position: 'absolute',
    top: -7,
    width: 0,
    height: 0,
    borderTopWidth: 5,
    borderTopColor: 'transparent',
    borderBottomWidth: 5,
    borderBottomColor: 'transparent',
    borderRightWidth: 5,
    borderRightColor: 'rgba(0, 0, 0, 0.85)',
    rotate: '90deg'
  }
};
